/**
 * HLF Shoppable Live Video Reels Commerce Engine
 * Refactored for production: Scoped instances, IntersectionObserver, Accessibility, and Security.
 */
(function() {
  'use strict';

  class HlfShoppableReels {
    constructor(sectionElement) {
      this.root = sectionElement;
      this.modals = Array.from(this.root.querySelectorAll('[data-reels-modal]'));
      if (this.modals.length === 0) return;

      this.modal = null; // active modal
      this.timerInterval = null;
      this.commentInterval = null;
      this.likeInterval = null;
      this.globalViewerInterval = null;
      
      this.currentLikes = 12000;
      this.remainingSeconds = 30 * 60; // 30 minutes default
      
      this.previousFocusedElement = null;
      this.activeCard = null;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);

      this.init();
    }

    updateModalReferences() {
      if (!this.modal) return;
      this.videoContainer = this.modal.querySelector('.hlf-reels-modal__video-container');
      this.videoPlayer = this.modal.querySelector('[data-reels-video-player]');
      this.closeModalBtn = this.modal.querySelector('[data-reels-close-modal]');
      this.muteBtn = this.modal.querySelector('[data-reels-mute-toggle]');
      this.openCatalogBtn = this.modal.querySelector('[data-reels-open-catalog]');
      this.closeCatalogBtn = this.modal.querySelector('[data-reels-close-catalog]');
      this.catalogSheet = this.modal.querySelector('[data-reels-catalog-sheet]');
      this.timerBox = this.modal.querySelector('[data-reels-timer-display]');
      this.chatTicker = this.modal.querySelector('[data-reels-chat-ticker]');
      this.liveCountEl = this.modal.querySelector('[data-reels-live-count-num]');
      this.likeBtn = this.modal.querySelector('[data-reels-like-btn]');
      this.likeCount = this.modal.querySelector('[data-reels-like-count]');
    }

    init() {
      this.cards = Array.from(this.root.querySelectorAll('.hlf-reel-card'));
      this.initCardViewers();
      this.initCardVideos();
      this.bindEvents();
    }

    destroy() {
      // Clear all intervals
      this.clearAllIntervals();
      if (this.globalViewerInterval) clearInterval(this.globalViewerInterval);
      
      // Remove global listeners bound by this instance
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleKeydown);

      if (this.videoObserver) {
        this.videoObserver.disconnect();
      }
    }

    clearAllIntervals() {
      if (this.timerInterval) clearInterval(this.timerInterval);
      if (this.commentInterval) clearInterval(this.commentInterval);
      if (this.likeInterval) clearInterval(this.likeInterval);
      this.timerInterval = null;
      this.commentInterval = null;
      this.likeInterval = null;
    }

    initCardViewers() {
      this.cards.forEach(card => {
        const videoUrl = card.dataset.videoUrl;
        if (!videoUrl) return;
        
        // Generate a simple safe storage key based on URL
        const hash = videoUrl.split('').reduce((a,b)=>{a=((a<<5)-a)+b.charCodeAt(0);return a&a},0);
        const storageKey = `hlf_viewers_${Math.abs(hash)}`;
        
        let count = this.parseCompactNumber(card.dataset.liveCount);
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          count = parseInt(stored, 10);
        }
        
        card.currentLiveViewers = count;
        card.dataset.storageKey = storageKey;
        this.updateCardViewerUI(card);
      });

      // Background fluctuation for all cards in this section
      this.globalViewerInterval = setInterval(() => {
        this.cards.forEach(card => {
          if (!card.currentLiveViewers) return;
          
          const delta = Math.floor(Math.random() * 29) - 14; 
          card.currentLiveViewers = Math.max(1200, card.currentLiveViewers + delta);
          sessionStorage.setItem(card.dataset.storageKey, card.currentLiveViewers);
          
          this.updateCardViewerUI(card);
          
          // If this card is active in the modal, sync the modal UI
          if (this.activeCard === card && this.liveCountEl) {
            const formatStr = (card.currentLiveViewers / 1000).toFixed(1) + 'K';
            this.liveCountEl.textContent = formatStr;
          }
        });
      }, 4500);
    }

    updateCardViewerUI(card) {
      const badge = card.querySelector('.hlf-reel-card__count-badge');
      if (badge) {
        const formatStr = (card.currentLiveViewers / 1000).toFixed(1) + 'K';
        badge.textContent = `👁 ${formatStr}`;
      }
    }

    initCardVideos() {
      const cardVideos = this.root.querySelectorAll('.hlf-reel-card__video');
      
      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting && (!this.modal || !this.modal.classList.contains('is-active'))) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, {
        rootMargin: '100px 0px',
        threshold: 0.25
      });

      cardVideos.forEach(v => {
        v.muted = true;
        this.videoObserver.observe(v);
      });
    }

    bindEvents() {
      // Use a single delegated document click listener for buttons that might be inside or outside the modal
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleKeydown);

      this.modals.forEach(modal => {
        const closeBtn = modal.querySelector('[data-reels-close-modal]');
        if (closeBtn) closeBtn.addEventListener('click', () => this.closeModal());

        modal.addEventListener('click', (e) => {
          if (e.target === modal) this.closeModal();
        });

        const muteBtn = modal.querySelector('[data-reels-mute-toggle]');
        if (muteBtn) {
          muteBtn.addEventListener('click', () => {
            if (!this.videoPlayer) return;
            this.videoPlayer.muted = !this.videoPlayer.muted;
            this.updateMuteIcon(this.videoPlayer.muted);
          });
        }

        const videoContainer = modal.querySelector('.hlf-reels-modal__video-container');
        if (videoContainer) {
          videoContainer.addEventListener('click', (e) => {
            if (e.target.closest('button') || e.target.closest('.hlf-reels-modal__overlay *')) return;
            if (!this.videoPlayer) return;

            if (this.videoPlayer.paused) {
              this.videoPlayer.play();
            } else {
              this.videoPlayer.pause();
            }
          });
        }

        const openCatalogBtn = modal.querySelector('[data-reels-open-catalog]');
        if (openCatalogBtn) {
          openCatalogBtn.addEventListener('click', () => modal.classList.add('has-catalog-open'));
        }

        const viewAllPill = modal.querySelector('[data-reels-view-all-pill]');
        if (viewAllPill) {
          viewAllPill.addEventListener('click', () => modal.classList.add('has-catalog-open'));
        }

        const closeCatalogBtn = modal.querySelector('[data-reels-close-catalog]');
        if (closeCatalogBtn) {
          closeCatalogBtn.addEventListener('click', () => modal.classList.remove('has-catalog-open'));
        }

        const likeBtn = modal.querySelector('[data-reels-like-btn]');
        if (likeBtn) {
          likeBtn.addEventListener('click', () => {
            if (!likeBtn.classList.contains('is-liked')) {
              likeBtn.classList.add('is-liked');
              this.currentLikes += 1;
              this.updateLikeDisplay();
            } else {
              likeBtn.classList.remove('is-liked');
              this.currentLikes -= 1;
              this.updateLikeDisplay();
            }
          });
        }
      });
    }

    handleDocumentClick(e) {
      // 1. Open Modal Trigger
      const card = e.target.closest('[data-open-reel-modal]');
      if (card && this.root.contains(card)) {
        e.preventDefault();
        const blockId = card.dataset.blockId;
        const targetModal = this.root.querySelector(`[data-reels-modal][data-block-id="${blockId}"]`);
        
        if (targetModal) {
          this.modal = targetModal;
          this.updateModalReferences();

          const videoUrl = card.dataset.videoUrl;
          const hostName = card.dataset.hostName;
          const hostAvatar = card.dataset.hostAvatar;
          const productTitle = card.dataset.productTitle || "";
          
          this.activeCard = card;
          this.openModal({ videoUrl, hostName, hostAvatar, productTitle });
        }
        return;
      }

      // 2. Add to Cart Trigger
      const addBtn = e.target.closest('[data-reels-add-to-cart]');
      if (addBtn && this.root.contains(addBtn)) {
        e.preventDefault();
        const variantId = addBtn.dataset.variantId;
        if (variantId) {
          this.addToCart(variantId, false, addBtn);
        }
        return;
      }

      // 3. Buy Now Trigger
      const buyNowBtn = e.target.closest('[data-reels-buy-now]');
      if (buyNowBtn && this.root.contains(buyNowBtn)) {
        e.preventDefault();
        const variantId = buyNowBtn.dataset.variantId;
        if (variantId) {
          this.addToCart(variantId, true, buyNowBtn);
        }
        return;
      }
    }

    handleKeydown(e) {
      if (e.key === 'Escape' && this.modal && this.modal.classList.contains('is-active')) {
        this.closeModal();
      }
    }

    parseCompactNumber(str) {
      if (!str) return 39400;
      const num = parseFloat(str);
      if (isNaN(num)) return 39400;
      if (str.toUpperCase().includes('K')) return Math.floor(num * 1000);
      if (str.toUpperCase().includes('M')) return Math.floor(num * 1000000);
      return Math.floor(num);
    }

    openModal({ videoUrl, hostName, hostAvatar, productTitle }) {
      if (!videoUrl) return;

      this.previousFocusedElement = document.activeElement;

      // Pause all background videos in this section
      const cardVideos = this.root.querySelectorAll('.hlf-reel-card__video');
      cardVideos.forEach(v => v.pause());

      // Safely Update host info
      const nameEl = this.modal.querySelector('.hlf-reels-modal__host-name');
      if (nameEl) nameEl.textContent = hostName || 'HealthFarm';
      
      const avatarBoxEl = this.modal.querySelector('.hlf-reels-modal__host-avatar-box');
      if (avatarBoxEl) {
        avatarBoxEl.replaceChildren(); // Safely clear

        if (hostAvatar) {
          try {
            const avatarUrl = new URL(hostAvatar, window.location.origin);
            if (['https:', 'http:'].includes(avatarUrl.protocol)) {
              const image = document.createElement('img');
              image.className = 'hlf-reels-modal__host-avatar';
              image.src = avatarUrl.href;
              image.alt = hostName || 'Host';
              image.style.width = '100%';
              image.style.height = '100%';
              image.style.objectFit = 'cover';
              image.style.borderRadius = '50%';
              image.style.display = 'block';
              avatarBoxEl.appendChild(image);
            }
          } catch (error) {
            console.warn('Invalid host avatar URL:', error);
            this.renderPlaceholderAvatar(avatarBoxEl);
          }
        } else {
          this.renderPlaceholderAvatar(avatarBoxEl);
        }
      }
      
      if (this.liveCountEl && this.activeCard) {
        const formatStr = (this.activeCard.currentLiveViewers / 1000).toFixed(1) + 'K';
        this.liveCountEl.textContent = formatStr;
      }

      // Load Video Source & Play
      if (this.videoPlayer) {
        this.videoPlayer.src = videoUrl;
        this.videoPlayer.muted = false; 
        this.updateMuteIcon(false);

        this.videoPlayer.play().catch(err => {
          console.warn('Autoplay with sound blocked, muting fallback:', err);
          this.videoPlayer.muted = true;
          this.updateMuteIcon(true);
          this.videoPlayer.play();
        });
      }

      // Show Modal & trap accessibility
      this.modal.classList.add('is-active');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      if (this.closeModalBtn) {
        this.closeModalBtn.focus();
      }

      // Start all simulations
      this.startSimulations(productTitle);
    }

    renderPlaceholderAvatar(boxEl) {
      boxEl.innerHTML = `<svg class="hlf-reels-modal__host-avatar-placeholder" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`;
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove('is-active');
      this.modal.classList.remove('has-catalog-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      if (this.videoPlayer) {
        this.videoPlayer.pause();
        this.videoPlayer.src = '';
      }

      // Stop all simulations
      this.clearAllIntervals();

      // Restore focus
      if (this.previousFocusedElement) {
        this.previousFocusedElement.focus();
      }

      // Let intersection observer resume videos if they are visible
      const cardVideos = this.root.querySelectorAll('.hlf-reel-card__video');
      cardVideos.forEach(v => {
        // A simple trick to re-trigger observer is to unobserve and observe
        if (this.videoObserver) {
          this.videoObserver.unobserve(v);
          this.videoObserver.observe(v);
        }
      });
    }

    startSimulations(productTitle) {
      this.clearAllIntervals(); // Ensure clean slate
      
      this.startCountdownTimer();
      this.startCommentTicker(productTitle);
      this.startLikeFluctuation();
    }

    startLikeFluctuation() {
      if (!this.likeCount || !this.likeBtn) return;

      this.currentLikes = Math.floor(Math.random() * 3000) + 12000;
      this.likeBtn.classList.remove('is-liked'); // reset state
      this.updateLikeDisplay();

      this.likeInterval = setInterval(() => {
        if (Math.random() > 0.5) {
          this.currentLikes += Math.floor(Math.random() * 3) + 1;
          this.updateLikeDisplay();
        }
      }, 4000);
    }

    updateLikeDisplay() {
      if (!this.likeCount) return;
      const kFormat = (this.currentLikes / 1000).toFixed(1) + 'K';
      this.likeCount.textContent = kFormat;
    }

    startCountdownTimer() {
      if (!this.timerBox) return;

      this.timerInterval = setInterval(() => {
        if (this.remainingSeconds <= 0) {
          this.remainingSeconds = 30 * 60; // Reset
        } else {
          this.remainingSeconds--;
        }

        const mins = Math.floor(this.remainingSeconds / 60);
        const secs = this.remainingSeconds % 60;
        const formatted = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

        this.timerBox.textContent = formatted;
      }, 1000);
    }

    startCommentTicker(productTitle) {
      if (!this.chatTicker) return;
      this.chatTicker.replaceChildren(); // Safe clear

      const pTitle = productTitle ? productTitle.split(' ').slice(0,2).join(' ') : "this";
      const sampleComments = [
        { user: "Anwar", text: `Price for ${pTitle}?` },
        { user: "Nur", text: `Is ${pTitle} available?` },
        { user: "Rahul", text: "Fast delivery to Delhi?" },
        { user: "Priya", text: `Just ordered ${pTitle} 🔥` },
        { user: "Aman", text: `Does ${pTitle} really work?` },
        { user: "Kavita", text: "Best quality products👌" },
        { user: "Ravi", text: `Any discount on ${pTitle}?` },
        { user: "Neha", text: "Quality is amazing!" }
      ];

      sampleComments.sort(() => 0.5 - Math.random());

      let idx = 0;
      this.commentInterval = setInterval(() => {
        const item = sampleComments[idx % sampleComments.length];
        idx++;

        const bubble = document.createElement('div');
        bubble.className = 'hlf-reels-chat-bubble';

        const userElement = document.createElement('span');
        userElement.className = 'hlf-reels-chat-user';
        userElement.textContent = `${item.user}:`;

        bubble.append(
          userElement,
          document.createTextNode(` ${item.text}`)
        );

        this.chatTicker.appendChild(bubble);

        while (this.chatTicker.children.length > 3) {
          this.chatTicker.removeChild(this.chatTicker.firstChild);
        }
      }, 3500);
    }

    updateMuteIcon(isMuted) {
      if (!this.muteBtn) return;
      const icon = this.muteBtn.querySelector('svg');
      if (!icon) return;

      if (isMuted) {
        icon.innerHTML = `<path d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
      } else {
        icon.innerHTML = `<path d="M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
      }
    }

    addToCart(variantId, isBuyNow = false, buttonEl = null) {
      let labelEl = null;
      let origText = '';
      
      if (buttonEl) {
        labelEl = buttonEl.querySelector('[data-button-label]') || buttonEl;
        buttonEl.disabled = true;
        origText = labelEl.textContent;
        labelEl.textContent = '...';
      }

      const shopifyRoot = window.hlfReelsRoutes?.root || window.Shopify?.routes?.root || '/';
      const cartAddUrl = window.hlfReelsRoutes?.cartAdd || `${shopifyRoot}cart/add.js`;
      const cartUrl = window.hlfReelsRoutes?.cart || `${shopifyRoot}cart`;
      const checkoutUrl = `${shopifyRoot}checkout`;

      fetch(cartAddUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json' 
        },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
      })
      .then(async res => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.description || data.message || `Unable to add product. Error ${res.status}`);
        }
        return data;
      })
      .then(data => {
        if (labelEl) {
          buttonEl.disabled = false;
          labelEl.textContent = '✓ Added!';
          setTimeout(() => {
            labelEl.textContent = origText;
          }, 2000);
        }

        if (isBuyNow) {
          const gokwikCheckoutBtn = document.querySelector('.gokwik-checkout button');
          if (gokwikCheckoutBtn) {
            gokwikCheckoutBtn.click();
          } else {
            window.location.href = checkoutUrl;
          }
        } else {
          // Trigger theme cart drawer
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            cartDrawer.open();
          } else {
            // Graceful fallback if drawer doesn't exist or open function is missing
            window.location.href = cartUrl;
          }
        }
      })
      .catch(err => {
        console.error('Add to cart failed:', err);
        if (labelEl) {
          buttonEl.disabled = false;
          labelEl.textContent = origText; // Revert without showing success
        }
        alert(err.message || 'Error adding item to cart. Please try again.');
      });
    }
  }

  // Initialization and Theme Editor support
  const initReels = () => {
    const sections = document.querySelectorAll('[data-hlf-reels-section]');
    sections.forEach(section => {
      if (!section.hlfReelsInstance) {
        section.hlfReelsInstance = new HlfShoppableReels(section);
      }
    });
  };

  document.addEventListener('DOMContentLoaded', initReels);

  // Shopify Design Mode Support
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector('[data-hlf-reels-section]');
      if (section) {
        section.hlfReelsInstance = new HlfShoppableReels(section);
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const section = event.target.querySelector('[data-hlf-reels-section]');
      if (section && section.hlfReelsInstance) {
        section.hlfReelsInstance.destroy();
        delete section.hlfReelsInstance;
      }
    });
  }
})();
